import React from "react";
import { useT, type DictKey } from "~/lib/i18n";

export default function Account(): React.JSX.Element {
  const { t } = useT();
  const plans: Array<{ id: string; label: string; price: string; period: string; emphasis: boolean; bulletKey: DictKey }> = [
    { id: "free", label: "Free", price: "0 €", period: "", emphasis: false, bulletKey: "web.account.bullet.free" },
    { id: "hunter", label: "Hunter", price: "5 €", period: t("landing.pricing.unitWeek"), emphasis: true, bulletKey: "web.account.bullet.hunter" },
    { id: "pro", label: "Hunter Pro", price: "12 €", period: t("landing.pricing.unitWeek"), emphasis: false, bulletKey: "web.account.bullet.pro" },
    { id: "agency", label: "Agency", price: "99 €", period: t("landing.pricing.unitMonth"), emphasis: false, bulletKey: "web.account.bullet.agency" },
  ];

  return (
    <div className="w-full max-w-[860px] mx-auto px-5 py-10">
      <span className="mono text-[10.5px] uppercase tracking-[0.12em] text-ink-tertiary">
        {t("web.account.kicker")}
      </span>
      <h1 className="mt-1 font-display text-[34px] leading-[42px] text-ink-primary">{t("web.account.h1")}</h1>

      <Section title={t("web.account.section.subscription")}>
        <p className="text-[13.5px] text-ink-secondary max-w-[60ch]">{t("web.account.subscription.body")}</p>
        <div className="mt-4 grid grid-cols-4 gap-3 text-[12.5px]">
          {plans.map((p) => (
            <PlanCard key={p.id} label={p.label} price={p.price} period={p.period} emphasis={p.emphasis} bullet={t(p.bulletKey)} />
          ))}
        </div>
      </Section>

      <Section title={t("web.account.section.notifications")}>
        <ul className="text-[13px] text-ink-secondary space-y-1.5">
          <li>· {t("web.account.notif.push")}</li>
          <li>· {t("web.account.notif.email")}</li>
          <li>· {t("web.account.notif.telegram")}</li>
          <li>· {t("web.account.notif.slack")}</li>
        </ul>
      </Section>

      <Section title={t("web.account.section.data")}>
        <p className="text-[13.5px] text-ink-secondary max-w-[60ch]">{t("web.account.data.body")}</p>
      </Section>
    </div>
  );
}

function PlanCard({
  label,
  price,
  period,
  emphasis,
  bullet,
}: {
  label: string;
  price: string;
  period: string;
  emphasis: boolean;
  bullet: string;
}): React.JSX.Element {
  return (
    <div
      className={
        "rounded-[4px] border p-3 " +
        (emphasis
          ? "border-[var(--accent)] bg-[var(--accent-soft)]"
          : "border-[var(--divider-strong)] bg-surface-card")
      }
    >
      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-tertiary">{label}</span>
      <p className="mt-1.5 font-display text-[22px] leading-[28px] text-ink-primary tabular-nums">
        {price}
        <span className="text-[12px] text-ink-secondary"> {period}</span>
      </p>
      <p className="mt-1.5 text-[11.5px] text-ink-secondary">{bullet}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section className="mt-10 border-t border-[var(--divider-strong)] pt-6">
      <h2 className="font-display text-[20px] leading-[28px] text-ink-primary">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}
